/**
 * Loads the intake form's controlled-value option sets from the reference-data
 * endpoints.
 *
 * ## What it reads, and why not the administration endpoints
 *
 * `GET /api/users/selectable` for owners and
 * `GET /api/master-data/:set/selectable` for the nine value sets. Not
 * `/api/admin/master-data/:set`, which is what this module's predecessor comment
 * proposed: those routes require `masterdata:admin`, a System_Administrator-only
 * capability, while the role that fills in an Initiative
 * (`commercial_planning`) does not hold it - so an intake user reading them is
 * denied at the capability layer before the handler runs. The selectable routes
 * also return the narrower set R16.5 permits as *new* selections (active and
 * effective as of the system date) rather than everything an administrator
 * maintains.
 *
 * ## Ten parallel requests, one loading state
 *
 * `useQueries` rather than ten `useQuery` calls so the sets are requested
 * concurrently and collapse into a single derived result. They are independent
 * reads; serialising them would make the form's first paint wait ten round trips
 * deep instead of one.
 *
 * ## R5.9, and why `referencedIds` is a parameter
 *
 * A field offers active values only, with one exception: a value the record under
 * edit already references stays selectable after deactivation, marked inactive, so
 * re-saving cannot silently drop it. That needs to be *asked for* - the server
 * cannot infer which record is being edited - so the caller passes the ids the
 * form currently holds. A blank intake passes none, which is why
 * {@link useIntakeOptions} is callable with no argument.
 */
import { useQueries, useQuery } from '@tanstack/react-query';
import { selectableUsersResponseSchema, type MasterDataSet } from '@complan/contracts';
import { apiFetch } from '@/shared/api/client';
import {
  fetchSelectableMasterData,
  type SelectableRecord,
} from '@/shared/api/selectable-master-data';
import { queryKeys } from '@/shared/query/query-keys';
import {
  EMPTY_OPTION_SETS,
  toControlledOptions,
  type ControlledOption,
  type IntakeOptionSets,
  type MasterOptionInput,
  type StoreTypeOption,
} from './intake-options';

/**
 * The ids the record under edit already references, per option set, so R5.9 can
 * retain a deactivated value. Every field is optional; a blank intake supplies
 * nothing.
 */
export interface ReferencedOptionIds {
  readonly owners?: readonly string[];
  readonly platforms?: readonly string[];
  readonly channels?: readonly string[];
  readonly audiences?: readonly string[];
  readonly customerGroups?: readonly string[];
  readonly categories?: readonly string[];
  readonly initiativeTypes?: readonly string[];
  readonly cscAttributes?: readonly string[];
  readonly priorities?: readonly string[];
  readonly milestoneTypes?: readonly string[];
  readonly fieldForceActivityTypes?: readonly string[];
}

/**
 * Which `IntakeOptionSets` field each wire set name populates.
 *
 * A fixed list of the nine Foundation intake-form value sets, deliberately
 * *not* every entry of `@complan/contracts`'s `MASTER_DATA_SETS` - that enum
 * grew three Manpower additions (`region`, `population-unit`, `pos-segment`,
 * `complan-manpower/requirements.md` Requirements 1, 2, 4) that are
 * Capacity/Demand Engine scope dimensions and Field Force POS references, not
 * Initiative Intake Form controlled-value fields the way the nine Foundation
 * sets are (R5.1-R5.10 name no Region or population-unit field on the intake
 * form). Iterating `MASTER_DATA_SETS` directly would silently try to fetch and
 * render dropdowns this form has no corresponding field for. `INTAKE_MASTER_DATA_SETS`
 * is the intake form's own closed list; a set added to `MASTER_DATA_SETS`
 * without a home in `OPTION_FIELD_BY_SET` no longer needs one unless the
 * Initiative Intake Form itself gains a matching field.
 */
const INTAKE_MASTER_DATA_SETS = [
  'platform',
  'channel',
  'customer-group',
  'audience',
  'category',
  'initiative-type',
  'priority',
  'milestone-type',
  'ff-activity-type',
  // The CSC dependency block's classification (migration 32).
  'csc-attribute',
] as const satisfies readonly MasterDataSet[];

const OPTION_FIELD_BY_SET: Readonly<
  Record<(typeof INTAKE_MASTER_DATA_SETS)[number], keyof Omit<ReferencedOptionIds, 'owners'>>
> = {
  platform: 'platforms',
  channel: 'channels',
  'customer-group': 'customerGroups',
  audience: 'audiences',
  category: 'categories',
  'initiative-type': 'initiativeTypes',
  priority: 'priorities',
  'milestone-type': 'milestoneTypes',
  'ff-activity-type': 'fieldForceActivityTypes',
  'csc-attribute': 'cscAttributes',
};

/**
 * The Manpower demand-input sets the Field Force step needs (Manpower R4.1,
 * R4.2, R4.6), kept separate from {@link INTAKE_MASTER_DATA_SETS}.
 *
 * Separate because they play different roles rather than out of tidiness. The
 * nine sets above populate `ReferencedOptionIds`-keyed controlled-value fields on
 * the Initiative itself; these three populate fields on a *Field Force scope
 * entry*, have no `OPTION_FIELD_BY_SET` home, and one of them (`pos-segment`)
 * resolves to a different option shape entirely because R4.2 needs each record's
 * POS count, not just its name.
 *
 * R5.9's retained-inactive rule does not apply to them: a Field Force scope entry
 * is replaced wholesale on every save, so there is no stored selection to preserve
 * across a deactivation the way an Initiative's Platform set is preserved.
 */
const MANPOWER_OPTION_SETS = ['region', 'population-unit'] as const satisfies
  readonly MasterDataSet[];

/** Which `IntakeOptionSets` field each Manpower set populates, in order. */
const MANPOWER_OPTION_FIELD_BY_SET = {
  region: 'regions',
  'population-unit': 'populationUnits',
} as const satisfies Record<(typeof MANPOWER_OPTION_SETS)[number], keyof IntakeOptionSets>;

/**
 * Reference data changes on an administrator's timescale, not a planner's, so a
 * multi-step wizard should not refetch ten sets every time a step remounts.
 */
const REFERENCE_STALE_TIME_MS = 5 * 60 * 1000;

function withIncludeIds(path: string, referencedIds: readonly string[]): string {
  if (referencedIds.length === 0) return path;
  const query = new URLSearchParams({ includeIds: referencedIds.join(',') });
  return `${path}?${query.toString()}`;
}

/**
 * The option builder's input, projected out of the cached records.
 *
 * A `select`, not part of the `queryFn`: `queryKeys.selectableMasterData(set, ids)`
 * is shared with the Calendar_View's filter bar, the Detail_Panel and the
 * Initiative list, and a cache entry holds exactly what its `queryFn` returned.
 * Narrowing to `{ id, name, isActive }` *before* the cache stripped `code`,
 * `colour`, `posCount` and `sourceTimestamp` from all of those whenever this
 * wizard happened to fetch the set first - see
 * `shared/api/selectable-master-data.ts` for the bug that caused. Narrowing after
 * the cache is per-observer and harms nobody.
 */
function toMasterOptionInputs(
  records: readonly SelectableRecord[],
): readonly MasterOptionInput[] {
  return records.map((record) => ({
    id: record.id,
    name: record.name,
    isActive: record.isActive,
    // Platform colour for the Classification step's swatches; `null` on every other set.
    colour: record.colour,
  }));
}

/**
 * Fetches the POS_Segment records as store-type options, carrying each one's
 * impacted POS count (R4.2, UI spec §6.2).
 *
 * `posCount` is the whole reason this set has its own fetch: R4.2 derives a scope
 * entry's Impacted_Population_Count by summing it across the selected segments, so
 * a name-only option would leave the derived count uncomputable.
 */
function toStoreTypeOptions(records: readonly SelectableRecord[]): readonly StoreTypeOption[] {
  return records.map((record) => ({
    id: record.id,
    label: record.name,
    posCount: record.posCount ?? 0,
    isInactive: !record.isActive,
  }));
}

/**
 * Fetches the assignable accounts.
 *
 * The label is `displayName`, with the email appended when two accounts share a
 * display name - which happens in a real directory and would otherwise leave the
 * user choosing between two identical-looking entries. Disambiguating only on
 * collision keeps the common case readable.
 */
async function fetchSelectableOwners(
  referencedIds: readonly string[],
): Promise<readonly ControlledOption[]> {
  const body = await apiFetch<unknown>(withIncludeIds('/api/users/selectable', referencedIds));
  const parsed = selectableUsersResponseSchema.parse(body);

  const nameCounts = new Map<string, number>();
  for (const user of parsed.items) {
    nameCounts.set(user.displayName, (nameCounts.get(user.displayName) ?? 0) + 1);
  }

  return parsed.items.map((user) => ({
    id: user.id,
    label: (nameCounts.get(user.displayName) ?? 0) > 1
      ? `${user.displayName} (${user.email})`
      : user.displayName,
    // The server returns an inactive account only when it was named in
    // `includeIds`, so `!isActive` is exactly R5.9's "retained because referenced".
    isInactive: !user.isActive,
  }));
}

/** What {@link useIntakeOptions} reports alongside the options themselves. */
export interface IntakeOptionsResult {
  readonly options: IntakeOptionSets;
  /** True until every set has resolved. The form renders against `EMPTY_OPTION_SETS` meanwhile. */
  readonly isLoading: boolean;
  /**
   * True when at least one set failed.
   *
   * Surfaced rather than swallowed: a form whose Owner list failed to load looks
   * identical to one where nobody is assignable, and silently offering an empty
   * required dropdown would make the user think the data is missing rather than the
   * request. The page decides how to present it.
   */
  readonly isError: boolean;
}

/**
 * The intake form's option sets.
 *
 * Returns `EMPTY_OPTION_SETS` while loading and on failure, so the form always has
 * a well-formed value to render and validate against - the shape never goes
 * undefined mid-render.
 *
 * `storeTypes` stays empty: Field Force store types with their POS counts are not
 * part of Foundation's data model (there is no such master data set and no POS
 * count anywhere in the schema), so there is nothing to load. That is a genuine
 * absence rather than an unwired one, and inventing a source here would be
 * inventing the manpower model.
 */
export function useIntakeOptions(referenced: ReferencedOptionIds = {}): IntakeOptionsResult {
  const ownerIds = referenced.owners ?? [];

  // Owners are a separate `useQuery` rather than the first entry of the
  // `useQueries` array below, because they resolve to a different type
  // (`ControlledOption`, already labelled) than the master data sets
  // (`MasterOptionInput`, labelled by `toControlledOptions`). In one heterogeneous
  // `useQueries` array TypeScript widens `data` to the union of both, which loses
  // the distinction at exactly the point it matters. Two hooks still issue their
  // requests in the same render, so nothing is serialised by the split.
  const ownersResult = useQuery({
    queryKey: queryKeys.selectableUsers(ownerIds),
    queryFn: () => fetchSelectableOwners(ownerIds),
    staleTime: REFERENCE_STALE_TIME_MS,
  });

  const setResults = useQueries({
    queries: INTAKE_MASTER_DATA_SETS.map((set) => {
      const ids = referenced[OPTION_FIELD_BY_SET[set]] ?? [];
      return {
        queryKey: queryKeys.selectableMasterData(set, ids),
        queryFn: () => fetchSelectableMasterData(set, ids),
        staleTime: REFERENCE_STALE_TIME_MS,
        select: toMasterOptionInputs,
      };
    }),
  });

  // The Manpower demand-input sets (Region, Population_Unit) plus the POS_Segment
  // store types, whose option shape differs - see MANPOWER_OPTION_SETS.
  const manpowerResults = useQueries({
    queries: MANPOWER_OPTION_SETS.map((set) => ({
      queryKey: queryKeys.selectableMasterData(set, []),
      queryFn: () => fetchSelectableMasterData(set, []),
      staleTime: REFERENCE_STALE_TIME_MS,
      select: toMasterOptionInputs,
    })),
  });
  const storeTypesResult = useQuery({
    queryKey: queryKeys.selectableMasterData('pos-segment', []),
    queryFn: () => fetchSelectableMasterData('pos-segment'),
    staleTime: REFERENCE_STALE_TIME_MS,
    select: toStoreTypeOptions,
  });

  const isLoading =
    ownersResult.isLoading ||
    setResults.some((result) => result.isLoading) ||
    manpowerResults.some((result) => result.isLoading) ||
    storeTypesResult.isLoading;
  const isError =
    ownersResult.isError ||
    setResults.some((result) => result.isError) ||
    manpowerResults.some((result) => result.isError) ||
    storeTypesResult.isError;

  if (isLoading || isError) {
    return { options: EMPTY_OPTION_SETS, isLoading, isError };
  }

  const options: IntakeOptionSets = {
    ...EMPTY_OPTION_SETS,
    owners: ownersResult.data ?? [],
    storeTypes: storeTypesResult.data ?? [],
    ...Object.fromEntries(
      MANPOWER_OPTION_SETS.map((set, index) => [
        MANPOWER_OPTION_FIELD_BY_SET[set],
        toControlledOptions(manpowerResults[index]?.data ?? [], []),
      ]),
    ),
    ...Object.fromEntries(
      INTAKE_MASTER_DATA_SETS.map((set, index) => {
        const field = OPTION_FIELD_BY_SET[set];
        const records = setResults[index]?.data ?? [];
        // `toControlledOptions` applies R5.9: active records first, then any
        // referenced-but-inactive record marked inactive. The server already
        // limited *which* records came back; this decides how they are presented.
        return [field, toControlledOptions(records, referenced[field] ?? [])];
      }),
    ),
  };

  return { options, isLoading: false, isError: false };
}
